import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { firstValueFrom } from 'rxjs';
import { PhotoboothService } from '../../services/photobooth.service';
import { ArtistService } from '../../services/artist.service';
import { FrameOwnershipService } from '../../services/frame-ownership.service';
import { FrameImagePreviewComponent } from '../../shared/frame-image-preview/frame-image-preview.component';
import { STORAGE_DELETE_MESSAGES, classifyStoragePath, deleteConfirmationFor } from '../../utils/storage-ownership.util';
import {
  extractFrameIdFromGeneratedName,
  FRAME_ASSET_CATEGORIES,
  STANDALONE_HOUSEKEEPING_MESSAGES
} from '../../utils/standalone-housekeeping.util';
import { ArtistLite, FRAME_OWNERSHIP_MESSAGES, fpImagePathFor, isAssignableArtist } from '../../utils/frame-ownership.util';

export interface FrameAsset {
  name: string;
  fullPath: string;
  url: string;
  timeCreated?: string;
  dateColor?: string;
  cutStyle?: string;
}

@Component({
  selector: 'app-frame-assets-manager',
  standalone: true,
  imports: [CommonModule, FormsModule, FrameImagePreviewComponent],
  templateUrl: './frame-assets-manager.component.html',
  styleUrls: ['./frame-assets-manager.component.css']
})
export class FrameAssetsManagerComponent implements OnInit {
  @Input() themeId: string = '';
  @Input() frameId: string = '';
  @Input() isGlobal: boolean = false;
  @Input() deviceId: string = '';
  @Input() version: string | null = null;
  @Input() tileType: string = '';
  @Output() closeManager = new EventEmitter<void>();

  private photoboothService = inject(PhotoboothService);
  private artistService = inject(ArtistService);
  private frameOwnership = inject(FrameOwnershipService);

  readonly ownershipText = FRAME_OWNERSHIP_MESSAGES;

  // KAN-149: frame owner (one artist per frame ID; null = Unassigned (company))
  allArtists: ArtistLite[] = [];
  assignableArtists: ArtistLite[] = [];
  artistsLoadFailed = false;
  uploadArtistId: string | null = null;
  uploadNotice: string | null = null;
  previewOwnerId: string | null = null;
  previewOwnerLoaded = false;
  editArtistId: string | null = null;
  ownerError: string | null = null;

  isLoading = false;
  isUploading: { [key: string]: boolean } = {
    _FTB: false,
    _FP: false,
    _PP: false,
    _FPBG: false
  };

  categorizedAssets: { [key: string]: FrameAsset[] } = {
    _FTB: [],
    _FP: [],
    _PP: [],
    _FPBG: []
  };

  /** True for a standalone device: its asset list never includes global files (KAN-517). */
  isStandaloneDevice = false;

  previewAsset: FrameAsset | null = null;
  
  selectedCategoryForPreview: string | null = null;

  // Upload Modal State
  isPreviewModalOpen: boolean = false;
  selectedFpFile: File | null = null;
  isUploadingPair: boolean = false;
  selectedFrameType: string = 'Strip';
  cutStyles: string[] = ['Strip', 'Multi'];
  selectedDateColor: string = '#696969';
  isSavingOrder: boolean = false;

  // Edit Settings State
  isEditingFrameSettings: boolean = false;
  editCutStyle: string = '';
  editDateColor: string = '#696969';
  isSavingFrameSettings: boolean = false;

  selectCategory(catId: string) {
    if (this.selectedCategoryForPreview === catId) {
      this.selectedCategoryForPreview = null;
    } else {
      this.selectedCategoryForPreview = catId;
    }
  }

  // KAN-523 AC-8: Include _PP (Print Preview) alongside _FTB, _FP, _FPBG per blueprint 5.2/13.2
  categories = [...FRAME_ASSET_CATEGORIES];

  get visibleCategories() {
    if (this.tileType === 'Signature') {
      return this.categories.filter(c => c.id !== '_FTB');
    }
    return this.categories;
  }

  async ngOnInit() {
    this.loadAssets();
    this.loadArtists();
    try {
      this.cutStyles = await this.photoboothService.getCutStyles();
      const stripStyle = this.cutStyles.find(s => s.toLowerCase() === 'strip');
      this.selectedFrameType = stripStyle || (this.cutStyles.length > 0 ? this.cutStyles[0] : 'Strip');
    } catch (e) {
      console.error('Error fetching cut styles:', e);
    }
  }

  /** KAN-149: artists for the owner selects. A failure never blocks uploads (the frame uploads as Unassigned). */
  async loadArtists() {
    try {
      const artists = await firstValueFrom(this.artistService.getArtists());
      this.allArtists = artists.map(a => ({ id: a.id, name: a.name, isActive: a.isActive, isDeleted: a.isDeleted }));
      this.assignableArtists = this.allArtists
        .filter(a => isAssignableArtist(a))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      this.artistsLoadFailed = false;
    } catch (e) {
      console.error('Error loading artists:', e);
      this.artistsLoadFailed = true;
    }
  }

  artistName(artistId: string | null): string {
    if (!artistId) return FRAME_OWNERSHIP_MESSAGES.unassigned;
    const artist = this.allArtists.find(a => a.id === artistId);
    if (!artist || artist.isDeleted) return `${artist?.name || artistId} (${FRAME_OWNERSHIP_MESSAGES.ownerDeleted.toLowerCase()})`;
    return artist.name || artistId;
  }

  /** Owner shown on the preview panel is no longer assignable (inactive or deleted): kept in the select so Edit never changes it. */
  get previewOwnerNotAssignable(): boolean {
    return !!this.previewOwnerId && !this.assignableArtists.some(a => a.id === this.previewOwnerId);
  }

  get previewOwnerInactive(): boolean {
    const artist = this.allArtists.find(a => a.id === this.previewOwnerId);
    return !!artist && artist.isDeleted !== true && artist.isActive === false;
  }

  isFpAsset(asset: FrameAsset | null): boolean {
    return !!asset && asset.name.toUpperCase().endsWith('_FP.PNG');
  }

  private frameIdOf(asset: FrameAsset): string {
    return extractFrameIdFromGeneratedName(asset.name);
  }

  private confirmOwnerChange(fromArtistId: string | null, toArtistId: string | null): boolean {
    if (!fromArtistId || fromArtistId === toArtistId) return true;
    return confirm(`This frame belongs to ${this.artistName(fromArtistId)}. Move it to ${this.artistName(toArtistId)}?\n\n${FRAME_OWNERSHIP_MESSAGES.allCopies}`);
  }

  /** Empty-state text for a category: the standalone wording when the machine has no artwork at all. */
  get emptyCategoryText(): string {
    const total = Object.values(this.categorizedAssets).reduce((n, list) => n + list.length, 0);
    return this.isStandaloneDevice && total === 0 ? STORAGE_DELETE_MESSAGES.emptyAssets : 'No images uploaded for this category yet.';
  }

  async loadAssets() {
    if (!this.themeId || !this.frameId) return;

    this.isLoading = true;
    try {
      this.isStandaloneDevice = !this.isGlobal && !!this.deviceId && await this.photoboothService.getDeviceOverrideGlobalLayout(this.deviceId);
      const allAssets = await this.photoboothService.getFrameAssets(this.isGlobal, this.deviceId, this.themeId, this.frameId, this.version);
      
      // Fetch the actual frame document to get uploadedFrames array which holds the true order
      const frames = await this.photoboothService.getFrames(this.isGlobal, this.deviceId, this.themeId, this.version);
      const currentFrame = frames.find(f => f.id === this.frameId);
      const uploadedFrames = currentFrame ? (currentFrame.uploadedFrames || []) : [];
      
      // Create an order map
      const orderMap = new Map<string, number>();
      const configMap = new Map<string, { cutStyle?: string, dateColor?: string }>();
      
      uploadedFrames.forEach((uf, idx) => {
        const fpName = uf.fpName || uf.name;
        if (fpName) {
          orderMap.set(fpName.toUpperCase(), idx);
          configMap.set(fpName.toUpperCase(), { cutStyle: uf.CutStyle, dateColor: uf.dateColor });
        }
        if (uf.ppName) configMap.set(uf.ppName.toUpperCase(), { cutStyle: uf.CutStyle, dateColor: uf.dateColor });
        if (uf.ftbName) configMap.set(uf.ftbName.toUpperCase(), { cutStyle: uf.CutStyle, dateColor: uf.dateColor });
        if (uf.fpbgName) configMap.set(uf.fpbgName.toUpperCase(), { cutStyle: uf.CutStyle, dateColor: uf.dateColor });
      });
      
      // Reset categorized
      this.categorizedAssets = { _FTB: [], _FP: [], _PP: [], _FPBG: [] };
      
      // Group by suffix (checking end of filename before .png)
      allAssets.forEach(asset => {
        const nameUpper = asset.name.toUpperCase();
        
        if (configMap.has(nameUpper)) {
          const config = configMap.get(nameUpper);
          asset.cutStyle = config?.cutStyle;
          asset.dateColor = config?.dateColor;
        }

        if (nameUpper.endsWith('_FTB.PNG')) this.categorizedAssets['_FTB'].push(asset);
        else if (nameUpper.endsWith('_FPBG.PNG')) this.categorizedAssets['_FPBG'].push(asset);
        else if (nameUpper.endsWith('_PP.PNG')) this.categorizedAssets['_PP'].push(asset);
        else if (nameUpper.endsWith('_FP.PNG')) this.categorizedAssets['_FP'].push(asset);
      });

      // Sort _FP array according to the database order
      this.categorizedAssets['_FP'].sort((a, b) => {
        const idxA = orderMap.has(a.name.toUpperCase()) ? orderMap.get(a.name.toUpperCase())! : Number.MAX_SAFE_INTEGER;
        const idxB = orderMap.has(b.name.toUpperCase()) ? orderMap.get(b.name.toUpperCase())! : Number.MAX_SAFE_INTEGER;
        return idxA - idxB;
      });
    } catch (error) {
      console.error('Error loading assets:', error);
      alert('Failed to load frame assets.');
    } finally {
      this.isLoading = false;
    }
  }

  async uploadImages(event: any, suffix: string): Promise<void> {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    this.isUploading[suffix] = true;
    try {
      let uploadedCount = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.name.toLowerCase().endsWith('.png')) {
          console.warn(`Skipping non-PNG file: ${file.name}`);
          continue;
        }
        const storagePath = await this.photoboothService.uploadFrameAsset(this.isGlobal, this.deviceId, this.themeId, this.frameId, file, suffix);
        const generatedName = storagePath.substring(storagePath.lastIndexOf('/') + 1);
        
        // KAN-523 AC-8: Shared frame anchor extracted from generated filename
        const baseFrameId = extractFrameIdFromGeneratedName(generatedName);
        
        const defaultCutStyle = this.cutStyles.find(s => s.toLowerCase() === 'strip') || (this.cutStyles.length > 0 ? this.cutStyles[0] : 'Strip');
        await this.photoboothService.addUploadedFrameAssetToGroup(
          this.isGlobal,
          this.deviceId,
          this.themeId,
          this.frameId,
          baseFrameId,
          { suffix: suffix, name: generatedName },
          defaultCutStyle
        );
        
        uploadedCount++;
      }
      
      if (uploadedCount > 0) {
        await this.loadAssets(); // Reload to show new items
      } else {
        alert('No valid PNG files were uploaded.');
      }
    } catch (error: any) {
      console.error(`Error uploading images for ${suffix}:`, error);
      // KAN-523 AC-9: Surface missing frame group error explicitly
      if (error?.message === STANDALONE_HOUSEKEEPING_MESSAGES.missingFrameGroupOnUpload) {
        alert(error.message);
      } else {
        alert(error?.message || 'Failed to upload some or all images.');
      }
    } finally {
      this.isUploading[suffix] = false;
      event.target.value = '';
    }
  }

  // --- Paired Upload Modal Logic ---

  openPreviewModal() {
    this.isPreviewModalOpen = true;
    this.selectedFpFile = null;
    this.uploadArtistId = null;
    this.uploadNotice = null;
    const stripStyle = this.cutStyles.find(s => s.toLowerCase() === 'strip');
    this.selectedFrameType = stripStyle || (this.cutStyles.length > 0 ? this.cutStyles[0] : 'Strip');
  }

  closePreviewModal() {
    this.isPreviewModalOpen = false;
    this.selectedFpFile = null;
  }

  onFpFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.png')) {
      this.selectedFpFile = file;
    } else {
      alert('Please select a valid PNG file.');
      event.target.value = '';
    }
  }

  async uploadPreviews() {
    if (!this.selectedFpFile) {
      alert('Frame Preview file is required.');
      return;
    }

    this.isUploadingPair = true;
    try {
      const generatedNames = await this.photoboothService.uploadPreview(
        this.isGlobal,
        this.deviceId,
        this.themeId,
        this.frameId,
        this.selectedFpFile
      );
      
      const baseFrameId = extractFrameIdFromGeneratedName(generatedNames.fpName);

      await this.photoboothService.addUploadedFrameAssetToGroup(
        this.isGlobal,
        this.deviceId,
        this.themeId,
        this.frameId,
        baseFrameId,
        { fpName: generatedNames.fpName },
        this.selectedFrameType,
        this.selectedDateColor
      );

      const ownerNote = await this.saveUploadOwner(baseFrameId, generatedNames.fpName);
      await this.loadAssets();
      this.closePreviewModal();
      this.uploadNotice = `Frame ${baseFrameId} uploaded. ${ownerNote}`;
    } catch (error: any) {
      console.error('Error uploading paired previews:', error);
      if (error?.message === STANDALONE_HOUSEKEEPING_MESSAGES.missingFrameGroupOnUpload) {
        alert(error.message);
      } else {
        alert(error?.message || 'Failed to upload preview pair.');
      }
    } finally {
      this.isUploadingPair = false;
    }
  }

  /**
   * KAN-149 AC-2: records the owner of an uploaded frame. A re-upload that keeps the frame ID keeps its owner unless another
   * artist is picked, and moving it from another artist needs a confirm. The upload itself never fails because of this step.
   */
  private async saveUploadOwner(frameId: string, fpName: string): Promise<string> {
    const fpPath = fpImagePathFor(this.isGlobal ? 'global' : 'device', this.deviceId, this.themeId, this.frameId, fpName);
    try {
      const existing = await this.frameOwnership.getOwnership(frameId);
      const current = existing && existing.isDeleted !== true ? existing.artistId ?? null : null;
      let target = this.uploadArtistId ?? current;
      if (this.uploadArtistId && !this.confirmOwnerChange(current, this.uploadArtistId)) target = current;
      await this.frameOwnership.setOwner(frameId, target, fpPath, current);
      return `Owner: ${this.artistName(target)}.`;
    } catch (e: any) {
      console.error('Error saving frame owner:', e);
      return `The owner could not be saved (${e?.message || 'unknown error'}); the frame stays as it was and can be assigned later.`;
    }
  }

  async deleteAsset(asset: FrameAsset) {
    const ownership = classifyStoragePath(asset.fullPath, this.isGlobal, this.deviceId);
    if (confirm(deleteConfirmationFor(ownership))) {
      try {
        const outcome = await this.photoboothService.deleteFrameAsset(asset.fullPath, this.isGlobal, this.deviceId);

        await this.photoboothService.removeUploadedFrameAssetFromGroup(
          this.isGlobal,
          this.deviceId,
          this.themeId,
          this.frameId,
          asset.name
        );

        await this.loadAssets();
        if (outcome === 'reference-only') alert(STORAGE_DELETE_MESSAGES.resultReferenceRemoved);
      } catch (error) {
        console.error('Error deleting asset:', error);
        alert('Failed to delete asset.');
      }
    }
  }

  openPreview(asset: FrameAsset) {
    this.previewAsset = asset;
    this.isEditingFrameSettings = false;
    this.ownerError = null;
    this.previewOwnerId = null;
    this.previewOwnerLoaded = false;
    if (this.isFpAsset(asset)) this.loadPreviewOwner(asset);
  }

  private async loadPreviewOwner(asset: FrameAsset) {
    try {
      const record = await this.frameOwnership.getOwnership(this.frameIdOf(asset));
      if (this.previewAsset !== asset) return;
      this.previewOwnerId = record && record.isDeleted !== true ? record.artistId ?? null : null;
      this.previewOwnerLoaded = true;
    } catch (e) {
      console.error('Error loading frame owner:', e);
      if (this.previewAsset === asset) this.ownerError = 'The owner could not be loaded.';
    }
  }

  closePreview() {
    this.previewAsset = null;
    this.isEditingFrameSettings = false;
  }

  startEditFrameSettings() {
    this.isEditingFrameSettings = true;
    this.editCutStyle = this.previewAsset?.cutStyle || 'Strip';
    this.editDateColor = this.previewAsset?.dateColor || '#696969';
    this.editArtistId = this.previewOwnerId;
    this.ownerError = null;
  }

  cancelEditFrameSettings() {
    this.isEditingFrameSettings = false;
  }

  async saveFrameSettings() {
    if (!this.previewAsset) return;
    const ownerChanged = this.isFpAsset(this.previewAsset) && this.previewOwnerLoaded && this.editArtistId !== this.previewOwnerId;
    if (ownerChanged && !this.confirmOwnerChange(this.previewOwnerId, this.editArtistId)) return;
    this.isSavingFrameSettings = true;
    this.ownerError = null;
    try {
      if (ownerChanged) {
        try {
          await this.frameOwnership.setOwner(this.frameIdOf(this.previewAsset), this.editArtistId, this.previewAsset.fullPath, this.previewOwnerId);
          this.previewOwnerId = this.editArtistId;
        } catch (e: any) {
          this.ownerError = e?.message || 'The owner could not be saved.';
          return;
        }
      }
      const baseFrameId = extractFrameIdFromGeneratedName(this.previewAsset.name);

      await this.photoboothService.addUploadedFrameAssetToGroup(
        this.isGlobal,
        this.deviceId,
        this.themeId,
        this.frameId,
        baseFrameId,
        {}, 
        this.editCutStyle,
        this.editDateColor
      );

      this.previewAsset.cutStyle = this.editCutStyle;
      this.previewAsset.dateColor = this.editDateColor;
      this.isEditingFrameSettings = false;
      await this.loadAssets();
    } catch (error) {
      console.error('Error saving frame settings:', error);
      alert('Failed to save frame settings.');
    } finally {
      this.isSavingFrameSettings = false;
    }
  }

  close() {
    this.closeManager.emit();
  }

  // --- Drag and Drop Ordering ---
  draggedAssetIndex: number | null = null;

  onDragStart(index: number, event: any) {
    if (this.version) return;
    this.draggedAssetIndex = index;
    event.dataTransfer.effectAllowed = 'move';
  }

  onDragOver(event: any) {
    if (this.version) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  async onDrop(index: number, event: any) {
    if (this.version) return;
    event.preventDefault();
    if (this.draggedAssetIndex === null || this.draggedAssetIndex === index) return;

    if (this.selectedCategoryForPreview === '_FP') {
      const arr = this.categorizedAssets['_FP'];
      if (!arr) return;

      moveItemInArray(arr, this.draggedAssetIndex, index);

      this.draggedAssetIndex = null;
    }
  }

  async saveOrder() {
    if (this.selectedCategoryForPreview !== '_FP') return;
    const arr = this.categorizedAssets['_FP'];
    if (!arr) return;

    this.isSavingOrder = true;
    const newFpNamesOrder = arr.map(a => a.name);

    try {
      await this.photoboothService.reorderUploadedFrames(
        this.isGlobal,
        this.deviceId,
        this.themeId,
        this.frameId,
        newFpNamesOrder
      );
      alert('Order saved successfully!');
    } catch (err) {
      console.error('Error saving order:', err);
      alert('Failed to save order.');
    } finally {
      this.isSavingOrder = false;
    }
  }
}
